import React from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { ShieldCheck } from "lucide-react";
import { IoClose } from "react-icons/io5";
import "./UnblockConfirmationModal.css";

export interface UnblockConfirmationModalProps {
  show: boolean;
  onHide?: () => void;
  onClose?: () => void;
  onConfirm?: () => void | Promise<void>;
  submit?: () => void | Promise<void>;
  userName?: string;
  title?: string;
  message?: React.ReactNode;
  confirmText?: string;
  buttonText?: string;
  cancelText?: string;
  isUnblocking?: boolean;
  loading?: boolean;
  disabled?: boolean;
}

const UnblockConfirmationModal: React.FC<UnblockConfirmationModalProps> = ({
  show,
  onHide,
  onClose,
  onConfirm,
  submit,
  userName,
  title,
  message,
  confirmText,
  buttonText,
  cancelText = "Cancel",
  isUnblocking = false,
  loading = false,
  disabled = false,
}) => {
  const isLoading = isUnblocking || loading;

  const handleClose = () => {
    if (onHide) onHide();
    else if (onClose) onClose();
  };

  const candidateName = userName || "this user";
  const displayTitle = title || `Unblock ${candidateName}`;
  const displayMessage =
    message !== undefined
      ? message
      : `Are you sure you want to unblock ${candidateName}? Once unblocked, they will regain access immediately.`;

  const primaryBtnText = confirmText || buttonText || "Unblock";

  const handleAction = async () => {
    const fn = onConfirm || submit;
    if (fn) await fn();
  };

  return (
    <Modal className="ChatConfirmModal UnblockConfirmModal" show={show} onHide={isLoading ? undefined : handleClose} centered backdrop="static">
      <Modal.Body className="p-4 p-sm-4 position-relative">
        {/* Close Button */}
        <button
          type="button"
          className="position-absolute top-0 end-0 m-3 btn btn-sm rounded-circle d-flex align-items-center justify-content-center border-0 text-muted"
          style={{
            width: "32px",
            height: "32px",
            backgroundColor: "#F1F5F9",
            zIndex: 1055,
            transition: "all 0.2s ease",
            cursor: "pointer"
          }}
          onClick={handleClose}
          disabled={isLoading}
          title="Close modal"
        >
          <IoClose size={18} />
        </button>

        {/* Icon Badge */}
        <div
          className="d-flex align-items-center justify-content-center mx-auto mb-3 shadow-sm"
          style={{
            width: 64,
            height: 64,
            borderRadius: "20px",
            background: "rgba(0, 31, 104, 0.08)",
            color: "#001F68",
            fontSize: "26px",
            border: "1px solid rgba(0, 31, 104, 0.15)"
          }}
        >
          <ShieldCheck size={28} />
        </div>

        {/* Title & Message */}
        <div className="text-center px-1">
          <h3 className="mb-2 fw-bold" style={{ fontSize: "20px", color: "#0F172A", letterSpacing: "-0.3px" }}>
            {displayTitle}
          </h3>
          {displayMessage && (
            <div className="mb-4 text-secondary" style={{ fontSize: "14px", lineHeight: "1.5", color: "#64748B" }}>
              {displayMessage}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="d-flex flex-column flex-sm-row gap-2 justify-content-center mt-4">
          <Button
            type="button"
            className="w-100 py-2 fw-semibold"
            style={{
              borderRadius: "12px",
              fontSize: "14px",
              height: "44px",
              backgroundColor: "#F1F5F9",
              color: "#475569",
              border: "1px solid #E2E8F0"
            }}
            onClick={handleClose}
            disabled={isLoading}
          >
            {cancelText}
          </Button>

          <Button
            type="button"
            className="w-100 py-2 border-0 fw-semibold shadow-sm d-flex align-items-center justify-content-center gap-2"
            style={{
              borderRadius: "12px",
              fontSize: "14px",
              height: "44px",
              backgroundColor: "#001F68",
              color: "#FFFFFF",
              transition: "all 0.2s ease"
            }}
            onClick={handleAction}
            disabled={isLoading || disabled}
          >
            {isLoading ? (
              <>
                <Spinner animation="border" size="sm" variant="light" />
                <span>Unblocking...</span>
              </>
            ) : (
              primaryBtnText
            )}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default UnblockConfirmationModal;
